import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Alert, Checkbox, Divider, Typography } from 'shared/ui'
import { PageDescription } from 'pages/settings/ui/PageDescription/PageDescription'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import { useRcsWizardContext } from 'pages/settings/pages/rcsV2/context/rcsWizardContext'
import { RcsWizardSteps } from 'pages/settings/pages/rcsV2/route/type'
import { RCS_SUPPORTED_COUNTRIES } from 'pages/settings/pages/rcsV2/lib/constants'
import { CountryRow } from './CountryRow/CountryRow'

import styles from './styles.module.scss'

const FEES_ALERT_TITLE = 'Fees required for RCS registration in the US'
const FEES_ALERT_DESC =
  'Non-refundable fees apply after your registration is approved and submitted for carrier review.'

const FEES_NOTE = (
  <>
    These are pass-through fees required for RCS registration in the US.
    <br />
    Fees cover brand verification and carrier submission.
    <br />
    There is no markup.
  </>
)

const FEE_ITEMS: { label: string; price: string }[] = [
  { label: 'Brand Verification Fees (recurring)', price: '$200 / year' },
  { label: 'T-Mobile Submission Fees (one-time)', price: '$500' },
]

const PAYMENT_CONSENT_LABEL = (
  <>
    I understand and agree to pay the US RCS onboarding fees.
    <br />
    Charges apply after registration approval and carrier review submission.
  </>
)

export const Countries = observer(() => {
  const { countriesStore, nextStepClick, saving } = useRcsWizardContext()

  const onNext = () => nextStepClick(RcsWizardSteps.countries)

  return (
    <div className={styles.wrap}>
      <PageDescription>
        Select the countries where you want to enable your RCS sender. Requirements vary by country,
        and we’ll guide you through the registration process.
      </PageDescription>

      {countriesStore.hasUsSelected && (
        <Alert
          className={styles.feesAlert}
          item={{ type: 'warning', title: FEES_ALERT_TITLE, desc: FEES_ALERT_DESC }}
          ariaLabel='RcsCountriesFees'
        />
      )}

      <div
        className={classNames(styles.list, {
          [styles.listWithAlert]: countriesStore.hasUsSelected,
        })}
      >
        {RCS_SUPPORTED_COUNTRIES.map(({ isoCode, label }) => (
          <CountryRow
            key={isoCode}
            country={isoCode}
            label={label}
            checked={countriesStore.isCountryActive(isoCode)}
            onToggle={countriesStore.toggleCountry}
          />
        ))}
      </div>

      {countriesStore.hasUsSelected && (
        <>
          <div className={styles.feeBox}>
            <Typography
              className={styles.feeNote}
              variant='body-md-regular'
              ariaLabel='RcsCountriesFees_note'
            >
              {FEES_NOTE}
            </Typography>
            <Divider className={styles.feeDivider} />
            <div className={styles.feeRows}>
              {FEE_ITEMS.map(({ label, price }) => (
                <div key={label} className={styles.feeRow}>
                  <Typography variant='body-md-regular' ariaLabel='RcsCountriesFees_item'>
                    {label}
                  </Typography>
                  <Typography variant='body-md-regular' ariaLabel='RcsCountriesFees_price'>
                    {price}
                  </Typography>
                </div>
              ))}
            </div>
          </div>

          <label className={styles.consent}>
            <Checkbox
              className={styles.consentCheckbox}
              checked={countriesStore.paymentConsent}
              onChange={countriesStore.togglePaymentConsent}
              size={16}
              ariaLabel='RcsCountriesConsent_checkbox'
            />
            <Typography
              variant='text-input-field-r'
              color='var(--content-primary-secondary)'
              ariaLabel='RcsCountriesConsent_label'
            >
              {PAYMENT_CONSENT_LABEL}
            </Typography>
          </label>
        </>
      )}

      <NextButton
        text='Next'
        disabled={countriesStore.isNextDisabled || saving}
        loading={saving}
        onClick={onNext}
      />
    </div>
  )
})
